import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import _get from "lodash/get"
import debounce from "lodash.debounce"
import { useSelector, useDispatch } from "react-redux"
import { isMobile } from "react-device-detect"
import { getUserPersona, sanitizeDOMString } from "@/utils/helper"
import {
  getInspirationDetails,
  selectInspirationState,
} from "@/store/features/inspirationSlice"
import { getConfig } from "@/constants/config"
import { useTranslation } from "next-i18next"

import { selectAuthState } from "@/store/features/authSlice"
import InspirationLandingList from "@/components/InspirationLanding/v1/InspirationLandingList.view"
import { selectFavoritesState } from "@/store/features/favoritesSlice"

const InspirationLanding = props => {
  const { data: authorData = {} } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { inspirations } = useSelector(selectInspirationState)
  const { favorites } = useSelector(selectFavoritesState)
  const { isAuth } = useSelector(selectAuthState)
  const [locale, setLocale] = useState("")
  const [currencySign, setCurrencySign] = useState("")
  const [currencyCode, setCurrencyCode] = useState("")
  const favoritesState = favorites?.textLineItems ?? []
  useEffect(() => {
    const configFn = async () => {
      const { locale, currencySign, currencyCode } = await getConfig()
      setLocale(locale)
      setCurrencySign(currencySign)
      setCurrencyCode(currencyCode)
    }
    configFn()
  }, [])
  const persona = getUserPersona()

  const [componentProps, setComponentProps] = useState({})
  const [rows, setRows] = useState(isMobile ? 30 : 30)
  const [start, setStart] = useState(0)

  const [sorts, setSorts] = useState([
    { name: t("kf.plp.label.bestSellers"), query: "productName_t+asc" },
    {
      name: t("kf.plp.label.priceDesc"),
      query: `priceList.${persona}.finalPrice_d+desc`,
    },
    {
      name: t("kf.plp.label.priceAsc"),
      query: `priceList.${persona}.finalPrice_d+asc`,
    },
    { name: t("kf.plp.label.atoz"), query: "Tile_Title+asc" },
    { name: t("kf.plp.label.ztoa"), query: "Tile_Title+desc" },
  ])
  const [selectedSort, setSelectedSort] = useState(null)
  const [curFilterQueries, setCurFilterQueries] = useState([])
  const [productsPerPage, setProductsPerPage] = useState([30, 60, 90])
  const [xfHtml, setXfHtml] = useState("")
  const [urlTitle, setUrlTitle] = useState("")
  const prevSortRef = useRef({
    name: t("kf.plp.label.featured"),
    query: ``,
  })
  const prevStartRef = useRef(0)
  const prevRowsRef = useRef(isMobile ? 30 : 30)
  const [currentEvent, setCurrentEvent] = useState("")

  useEffect(() => {
    // to scrollup the product list
    window.scrollTo(0, 0)
    try {
      const queries = setFacets()
      getInspirationItems(queries)
    } catch (error) {
      console.error(error)
    }
  }, [isAuth])

  const setFacets = () => {
    const urlSearchParams = new URLSearchParams(window.location.search)
    let queries = []
    urlSearchParams.forEach((values, filterName) => {
      let multiValues = values.split(",")
      let displayNames = []
      let displayValues = []
      multiValues.forEach(facetsValues => {
        let displayValue = facetsValues
        if (facetsValues.includes("|")) {
          displayValue = facetsValues.split("|")[0]
        }
        displayNames.push(facetsValues)
        displayValues.push(displayValue)
      })
      queries.push({
        facet: filterName,
        value: displayNames,
        display: displayValues,
      })
    })
    return queries
  }

  const sanitizeText = text => text.replace(/<\/?[^>]+(>|$)/g, "")

  const getSearchResult = debounce((query, update, search, cb) => {
    dispatch(
      getInspirationDetails({ query, update, isSingle: false, search, cb })
    )
  }, 1000)

  const getInspirationItems = (queries, update, search = null, cb = null) => {
    let newStart = 0
    // save queries to handle sorting
    if (queries) {
      setCurFilterQueries(queries)
    } else {
      queries = curFilterQueries
      newStart = start
    }
    if (!update) {
      setStart(0)
    }

    // build lucid work query and make http request
    let fq = []
    queries.forEach(q => {
      const fc = q.facet.replace(/^\*{2}/, "")
      const encodedFilterValues = q.value.map(queryValue =>
        encodeURIComponent(queryValue)
      )
      const tagValue = encodeURIComponent(`{!tag="${fc}"}`)
      fq.push(`${tagValue}${fc}:("${encodedFilterValues.join('" OR "')}")`)
    })

    let query = { fq, rows, start: newStart }
    if (selectedSort) query.sort = selectedSort
    getSearchResult(query, update, search, cb)
  }

  const handleSort = query => {
    setSelectedSort(query)
    setStart(0)
    setCurrentEvent("sorting")
    getInspirationItems()
  }

  const handleRows = rows => {
    setRows(rows)
    setStart(0)
    setCurrentEvent("pagination")
    sessionStorage.setItem("rows", rows)
  }

  const handleLoadMore = () => {
    setStart(start + rows)
    setRows(30)
    setCurrentEvent("loadmore")
  }

  useEffect(() => {
    // Check if either selectedSort or start or row has changed
    if (
      selectedSort?.name !== prevSortRef.current?.name &&
      currentEvent === "sorting"
    ) {
      getInspirationItems() // Update the previous values
      prevSortRef.current = selectedSort
    }
    if (start !== prevStartRef.current && currentEvent === "loadmore") {
      getInspirationItems(null, true) // Update the previous values
      prevStartRef.current = start
    }
    if (rows !== prevRowsRef.current && currentEvent === "pagination") {
      getInspirationItems() // Update the previous values
      prevRowsRef.current = rows
    }
  }, [selectedSort, start, rows, currentEvent])

  const handleFilter = (queries, update, search = null, cb = null) => {
    setStart(0)
    setCurrentEvent("filter")
    getInspirationItems(queries, update, search, cb)
  }

  const {
    data = [],
    total,
    facets = [],
    loading = true,
    colors,
    facetLabels,
  } = inspirations || {}

  const i18n = useMemo(
    () => ({
      matchNotFound: "Matches not found.",
      otherCombination: "Please try another combination.",
      loading: "Loading ...",
      pleaseWait: "Please wait.",
      noDataFound: "No Data Found.",
    }),
    []
  )

  const staticTexts = useMemo(
    () => ({
      totalCount: t("kf.plp.label.totalCount", { count: total }),
      totalCount: t("kf.plp.label.totalCount"),
      hideFilterAria: t("kf.plp.label.hideFilterAria"),
      showFilterAria: t("kf.plp.label.showFilterAria"),
      clearAllAria: t("kf.plp.label.clearAllAria"),
      listWith: t("kf.plp.label.listWith"),
      items: t("kf.plp.label.inspirationItems"),
      facetListAriaExpandOrCollapse: t(
        "kf.plp.label.facetListAriaExpandOrCollapse"
      ),
      showMoreFiltersAria: t("kf.plp.label.showMoreFiltersAria"),
      sortByAria: t("kf.plp.label.sortByAria", { sortByCount: sorts.length }),
      salePriceAria: t("kf.plp.label.salePriceAria"),
      originalPriceAria: t("kf.plp.label.originalPriceAria"),
      productDescription: t("kf.plp.label.productDescription"),
      colorsLinkAria: t("kf.plp.label.colorsLinkAria"),
      pageTitleAria: t("kf.plp.label.pageTitleAria", {
        categoryName: componentProps.categoryName,
        brandName: "Kohler",
      }),
      print: t("kf.plp.label.print"),
      share: t("kf.plp.label.share"),
      wishlist: t("kf.plp.label.wishlist"),
      showFilter: t("kf.plp.label.showFilter"),
      hideFilter: t("kf.plp.label.hideFilter"),
      filters: t("kf.plp.label.filters"),
      addToCompare: t("kf.plp.label.addToCompare"),
      colors: t("kf.plp.label.colors"),
      color: t("kf.plp.label.color"),
      view: t("kf.plp.label.view"),
      all: t("kf.plp.label.all"),
      sort: t("kf.plp.label.sort"),
      applyFilters: "Apply Filters",
      results: t("kf.plp.label.results"),
      loadMore: t("kf.plp.label.loadMore"),
      backToTop: t("kf.plp.label.backToTop"),
      showMore: t("kf.plp.label.showMore"),
      showLess: t("kf.plp.label.showLess"),
      viewAll: t("kf.pdp.label.viewMore"),
      viewLess: t("annsacks.orderSample.label.viewLess"),
      clearAll: t("kf.plp.label.clearAll"),
      priceRangeTitle: t("kf.plp.label.priceRangeTitle"),
      priceRangeDesc: t("kf.plp.label.priceRangeDesc"),
      to: t("kf.plp.label.to"),
      minText: t("kf.plp.label.minText"),
      maxText: t("kf.plp.label.maxText"),
      sameCategory: componentProps.sameCategory
        ? componentProps.sameCategory
        : t("kf.compare.validation.sameCategory"),
      maxProduct: componentProps.maxProduct
        ? componentProps.maxProduct
        : t("kf.compare.validation.maxProducts"),
      favSuccessMsg: t("kf.favorites.success"),
      favErrorMsg: t("kf.favorites.error"),
      errors: {
        validRange: t("kf.plp.error.validRange"),
        minGreaterThanMax: t("kf.plp.error.minGreaterThanMax"),
        maxGreaterThanZero: t("kf.plp.error.maxGreaterThanZero"),
        checkValid: t("kf.error.message.price"),
      },
      startingAt: t("kf.plp.label.startingAt"),
      new: componentProps.newBadge
        ? componentProps.newBadge
        : t("kf.plp.label.new"),
      sale: componentProps.sale ? componentProps.sale : t("kf.plp.label.sale"),
      viewResults: t("kf.plp.label.viewResults"),
      exclusive: t("kf.plp.label.exclusive"),
      newExclusive: t("kf.plp.label.newExclusive"),
      removeItem: t("kf.favorites.removeItem"),
      removeItemError: t("kf.favorites.removeItemError"),
      backToResult: t("kf.plp.label.backToResult"),
    }),
    [t, total, componentProps, sorts.length]
  )
  return (
    <InspirationLandingList
      loading={loading}
      componentProps={componentProps}
      curRows={rows}
      data={data}
      sorts={sorts}
      facets={facets}
      facetLabels={facetLabels}
      totalResults={total}
      onFilter={handleFilter}
      onSort={handleSort}
      onLoadMore={handleLoadMore}
      onRows={handleRows}
      staticTexts={staticTexts}
      i18n={i18n}
      isCurated={componentProps?.type === "curated"}
      colors={colors}
      productsPerPage={productsPerPage}
      currFilters={curFilterQueries}
      selectedFilterCount={
        curFilterQueries &&
        curFilterQueries?.reduce((acc, item) => {
          return acc + item.value.length
        }, 0)
      }
      urlTitle={urlTitle}
      displayShare={authorData?.share}
      displayPrint={authorData?.print}
      enableFilter={authorData?.enableFilter}
      favorites={favoritesState}
      // cart={cart}
    />
  )
}

export default InspirationLanding
